/**
 * Starting the follow-up turn for background runs that finished.
 *
 * One turn per conversation at a time: runs that finish together become one prompt, and runs of a
 * conversation whose turn is still streaming wait until it ends. Starting a turn per run used to
 * stream several replies into the same thread at once, scrambling them, and had every reply repeat
 * the same work.
 */
import type {PendingRun} from './pendingRuns'
import {transferContinuationPrompt} from './transferContinuation'

/** The part of a conversation session the dispatcher needs. */
export interface ContinuationSession {
  readonly isRunning: boolean
  /** Start the turn; false when one is already running. */
  startContinuation: (prompt: string) => boolean
}

export interface ContinuationDispatchDeps {
  getResolvedRuns: () => PendingRun[]
  getSession: (conversationId: string) => ContinuationSession | undefined
  /** Win the right to continue a server-tracked run; false when another client has it. */
  claim: (runId: string) => Promise<boolean>
  release: (runId: string) => void
  remove: (runId: string) => void
  onClaimError?: (runId: string, error: unknown) => void
}

/**
 * The sentence for one run, worded as before batching so a single run reads the same. It is also the
 * user bubble of the follow-up turn, so it reads as the user asking rather than as agent instructions.
 */
export function runContinuationLine(entry: PendingRun): string {
  const transfer = entry.resolution?.transfer
  if (transfer) return transferContinuationPrompt(entry.runId, transfer)
  const status = entry.resolution?.status ?? 'complete'
  const label = entry.tool ? `${entry.tool} run` : 'run'
  if (status === 'complete') {
    return `My ${label} ${entry.runId} is complete. What do the results show?`
  }
  if (status === 'canceled') return `My ${label} ${entry.runId} was canceled. What happened?`
  return `My ${label} ${entry.runId} failed. What went wrong?`
}

/** One prompt for every run that is ready; a single run keeps its own wording. */
export function continuationPrompt(entries: readonly PendingRun[]): string {
  if (entries.length === 1) return runContinuationLine(entries[0])
  const lines = entries.map((entry) => `- ${runContinuationLine(entry)}`)
  return [
    `${entries.length} of my background runs have finished:`,
    ...lines,
    'Please go through them together in one reply.',
  ].join('\n')
}

function needsClaim(entry: PendingRun): boolean {
  return entry.origin === 'server' || Boolean(entry.resolution?.transfer)
}

function groupByConversation(entries: readonly PendingRun[]): Map<string, PendingRun[]> {
  const groups = new Map<string, PendingRun[]>()
  for (const entry of entries) {
    if (!entry.conversationId) continue
    groups.set(entry.conversationId, [...(groups.get(entry.conversationId) ?? []), entry])
  }
  return groups
}

async function claimAll(
  entries: readonly PendingRun[],
  deps: ContinuationDispatchDeps
): Promise<{won: PendingRun[]; claimed: string[]}> {
  const won: PendingRun[] = []
  const claimed: string[] = []
  for (const entry of entries) {
    if (!needsClaim(entry)) {
      won.push(entry)
      continue
    }
    let ok = false
    try {
      ok = await deps.claim(entry.runId)
    } catch (error) {
      deps.onClaimError?.(entry.runId, error)
    }
    if (ok) {
      won.push(entry)
      claimed.push(entry.runId)
    } else {
      // Someone else is continuing it. The run is still finished, so the tag goes either way.
      deps.remove(entry.runId)
    }
  }
  return {won, claimed}
}

/**
 * Start at most one follow-up turn per conversation for the runs that are ready. Runs stay marked
 * while their conversation has no live session or a turn running; the next call picks them up.
 */
export async function dispatchResolvedRuns(deps: ContinuationDispatchDeps): Promise<void> {
  for (const [conversationId, entries] of groupByConversation(deps.getResolvedRuns())) {
    const session = deps.getSession(conversationId)
    if (!session || session.isRunning) continue

    // Claim before dispatching. Two devices holding the same run both see it resolve, and without
    // this both would post the follow-up turn. A run the server doesn't track has no row to claim.
    const {won, claimed} = await claimAll(entries, deps)
    if (won.length === 0) continue

    // The claims took a round trip; the user may have started a turn meanwhile. Keep the runs and
    // hand the claims back so the next drain (or the cloud invoker) can take them.
    const started = !session.isRunning && session.startContinuation(continuationPrompt(won))
    if (started) won.forEach((entry) => deps.remove(entry.runId))
    claimed.forEach((runId) => deps.release(runId))
  }
}
